import React, { useState, useEffect, useRef } from 'react';
import {
  Bus,
  Shield,
  MapPin,
  Bell,
  Smartphone,
  ArrowRight,
  ChevronDown,
  ChevronUp,
  Phone,
  Mail,
  TrendingUp,
  Zap,
  AlertCircle,
  MessageCircle,
  Building2,
  Navigation,
  Clock,
  CheckCircle,
  Users,
  FileCheck,
  Briefcase,
  BarChart3,
} from 'lucide-react';
import AnimatedBackground from '../components/AnimatedBackground';
import EmployeeResults from '../components/EmployeeResults';
import EmployeeTestimonials from '../components/EmployeeTestimonials';

const useScrollAnimation = () => {
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, []);

  return { ref, isVisible };
};

const EmployeeTransport: React.FC = () => {
  const [openFaq, setOpenFaq] = useState<number | null>(0);

  const features = [
    {
      icon: MapPin,
      title: 'Live Trip Visibility',
      description: 'Track every cab and route in real time with ETAs, route status, and deviation alerts.',
      color: 'from-blue-500 to-blue-600',
    },
    {
      icon: Bell,
      title: 'Employee Notifications',
      description: 'Send automatic pickup alerts, arrival updates, delay notices, and trip completion notifications.',
      color: 'from-orange-500 to-orange-600',
    },
    {
      icon: Shield,
      title: 'Safety & Escalations',
      description: 'Support night-shift transport with SOS workflows, exception alerts, and full trip audit trails.',
      color: 'from-green-500 to-green-600',
    },
    {
      icon: Zap,
      title: 'Shift-Based Scheduling',
      description: 'Plan pickups and drops around rotating rosters, shift changes, weekends, and peak traffic windows.',
      color: 'from-purple-500 to-purple-600',
    },
    {
      icon: FileCheck,
      title: 'Vendor & SLA Control',
      description: 'Track vendor performance, missed pickups, route adherence, and service quality from one dashboard.',
      color: 'from-pink-500 to-pink-600',
    },
    {
      icon: BarChart3,
      title: 'Billing & Reporting',
      description: 'Automate trip summaries, monthly reconciliation, billing validation, and compliance-ready reports.',
      color: 'from-red-500 to-red-600',
    },
  ];

  const faqs = [
    {
      q: 'How quickly can we deploy Employee Transport?',
      a: 'Most teams can go live in 2-4 weeks depending on route complexity, employee count, and vendor onboarding needs. We support rollout planning, master data setup, and admin training.',
    },
    {
      q: 'Can the platform handle multiple shifts and changing rosters?',
      a: 'Yes. The system is designed for round-the-clock operations and supports multiple shifts, roster changes, recurring routes, and exception handling.',
    },
    {
      q: 'Do employees need a separate app?',
      a: 'Employees can receive trip visibility, pickup alerts, and trip updates through the employee app experience designed for a simple daily commute workflow.',
    },
    {
      q: 'Can we manage multiple vendors in one place?',
      a: 'Yes. Vendor performance, route execution, SLA adherence, billing inputs, and escalations can all be monitored centrally.',
    },
    {
      q: 'How does GaadiMatic help with safety compliance?',
      a: 'The platform helps teams monitor active trips, track route deviations, maintain trip logs, and respond faster to exceptions or escalation events.',
    },
    {
      q: 'Is this suitable for enterprise campuses and distributed offices?',
      a: 'Yes. The product is built for enterprise transport across IT parks, BPOs, manufacturing sites, and multi-location operations.',
    },
  ];

  const FeatureCard: React.FC<{ feature: (typeof features)[0]; index: number }> = ({ feature, index }) => {
    const { ref, isVisible } = useScrollAnimation();
    const Icon = feature.icon;

    return (
      <div
        ref={ref}
        className={`
          group relative overflow-hidden rounded-2xl bg-white border border-gray-100 p-6
          hover:shadow-xl hover:shadow-primary-500/10 hover:-translate-y-1
          transition-all duration-500 ease-out
          ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}
        `}
        style={{ transitionDelay: `${index * 100}ms` }}
      >
        <div className={`absolute inset-0 bg-gradient-to-br ${feature.color} opacity-0 group-hover:opacity-5 transition-opacity duration-500`} />

        <div
          className={`
            relative w-14 h-14 rounded-2xl bg-gradient-to-br ${feature.color}
            flex items-center justify-center mb-4
            group-hover:scale-110 group-hover:rotate-3
            transition-all duration-500 shadow-lg
          `}
        >
          <Icon className="w-7 h-7 text-white" />
        </div>

        <h3 className="relative text-xl font-bold text-navy-950 mb-2 group-hover:text-primary-600 transition-colors">
          {feature.title}
        </h3>
        <p className="relative text-gray-600 leading-relaxed">{feature.description}</p>

        <div className="relative mt-4 flex items-center gap-2 text-primary-600 font-semibold opacity-0 group-hover:opacity-100 transform translate-x-[-10px] group-hover:translate-x-0 transition-all duration-300">
          <span>Learn more</span>
          <ArrowRight className="w-4 h-4" />
        </div>
      </div>
    );
  };

  const EmployeeAppMockup = () => (
    <div className="bg-white rounded-3xl shadow-sm overflow-hidden border border-gray-200">
      <div className="bg-gray-900 p-2">
        <div className="bg-white rounded-2xl overflow-hidden">
          <div className="bg-primary-600 px-4 py-3 flex items-center justify-between">
            <div className="text-white font-semibold">My Office Commute</div>
            <div className="w-8 h-8 bg-white/20 rounded-full" />
          </div>

          <div className="h-48 bg-gradient-to-br from-blue-50 to-blue-100 relative">
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-12 h-12 bg-primary-600 rounded-full flex items-center justify-center shadow-lg animate-pulse">
                <Bus className="w-6 h-6 text-white" />
              </div>
            </div>
            <div className="absolute bottom-4 left-4 right-4 bg-white rounded-xl p-3 shadow-lg">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                <span className="text-sm font-medium">Cab arriving in 4 mins</span>
              </div>
            </div>
          </div>

          <div className="p-4 space-y-3">
            {[
              { time: '8:16 PM', event: 'Driver assigned', status: 'completed' },
              { time: '8:28 PM', event: 'Cab approaching pickup point', status: 'current' },
              { time: '8:45 PM', event: 'Expected office arrival', status: 'pending' },
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-3">
                <div
                  className={`w-2 h-2 rounded-full ${
                    item.status === 'completed' ? 'bg-green-500' : item.status === 'current' ? 'bg-primary-600 animate-pulse' : 'bg-gray-300'
                  }`}
                />
                <div className="flex-1">
                  <div className="text-xs text-gray-500">{item.time}</div>
                  <div className="text-sm font-medium">{item.event}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  const AdminDeskMockup = () => (
    <div className="bg-white rounded-3xl shadow-sm overflow-hidden border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="text-lg font-bold">Transport Desk</div>
          <div className="text-sm text-gray-500">Shift Planning Overview</div>
        </div>
        <div className="px-3 py-1 bg-primary-100 text-primary-700 rounded-full text-xs font-semibold">
          Night Shift
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4 mb-6">
        {[
          { label: 'Routes', value: '18', change: '+4' },
          { label: 'Employees', value: '642', change: 'Live' },
          { label: 'No-Shows', value: '12', change: '-18%' },
        ].map((stat, i) => (
          <div key={i} className="bg-gray-50 rounded-xl p-3">
            <div className="text-2xl font-bold text-navy-950">{stat.value}</div>
            <div className="text-xs text-gray-500">{stat.label}</div>
            <div className="text-xs text-green-600 mt-1">{stat.change}</div>
          </div>
        ))}
      </div>

      <div className="space-y-3">
        {[
          { route: 'Route A12', detail: '28 seats allocated', status: 'On time' },
          { route: 'Route B08', detail: '3 pickup changes', status: 'Updated' },
          { route: 'Route C21', detail: 'Vendor confirmed', status: 'Ready' },
        ].map((item, i) => (
          <div key={i} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
            <div>
              <div className="text-sm font-semibold text-navy-950">{item.route}</div>
              <div className="text-xs text-gray-500">{item.detail}</div>
            </div>
            <div className="text-xs font-medium text-primary-600">{item.status}</div>
          </div>
        ))}
      </div>
    </div>
  );

  const ControlTowerMockup = () => (
    <div className="bg-white rounded-3xl shadow-sm overflow-hidden border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="text-lg font-bold">Operations Control Tower</div>
          <div className="text-sm text-gray-500">Live Fleet and SLA Monitoring</div>
        </div>
        <div className="flex gap-2">
          <div className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-semibold">26 Active</div>
          <div className="px-3 py-1 bg-yellow-100 text-yellow-700 rounded-full text-xs font-semibold">2 Alerts</div>
        </div>
      </div>

      <div className="h-40 bg-gradient-to-br from-green-50 to-blue-50 rounded-xl relative overflow-hidden mb-5">
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="grid grid-cols-4 gap-4">
            {[...Array(8)].map((_, i) => (
              <div key={i} className={`w-3 h-3 rounded-full ${i % 3 === 0 ? 'bg-green-500 animate-pulse' : 'bg-primary-600'}`} />
            ))}
          </div>
        </div>
        <div className="absolute bottom-3 left-3 bg-white rounded-lg px-3 py-1 shadow-sm text-xs font-medium">
          Active routes in progress
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="bg-gray-50 rounded-xl p-4">
          <div className="text-xs text-gray-500 mb-1">SLA Adherence</div>
          <div className="text-2xl font-bold text-navy-950">99.5%</div>
        </div>
        <div className="bg-gray-50 rounded-xl p-4">
          <div className="text-xs text-gray-500 mb-1">Vendor Score</div>
          <div className="text-2xl font-bold text-navy-950">4.8/5</div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-white">
      <AnimatedBackground />

      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-32 overflow-hidden bg-gray-50">
        <div className="absolute inset-0 bg-gradient-to-br from-primary-50/50 via-transparent to-accent-orange/5" />
        <div className="container-main relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary-100 rounded-full mb-6 animate-fade-in">
              <Briefcase className="w-4 h-4 text-primary-600" />
              <span className="text-sm font-medium text-primary-700">Enterprise Employee Transport</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-navy-950 leading-tight mb-6">
              Safe Commutes. <span className="text-primary-600">Predictable Shifts.</span> <br />
              Complete Transport Control.
            </h1>
            <p className="text-lg sm:text-xl text-gray-600 leading-relaxed max-w-3xl mx-auto mb-10">
              The employee transport platform for HR teams, transport desks, and enterprise operations.
              Manage planning, live trips, vendor performance, and employee experience from one place.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="#contact" className="btn-primary group">
                Request Demo
              </a>
              <a href="/pricing" className="btn-secondary">
                View Pricing
                <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
              </a>
            </div>
          </div>
        </div>
      </section>

      <EmployeeResults />

      <section className="section-padding bg-white">
        <div className="container-main">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-navy-950 mb-6">
                The Challenge with Enterprise Commute Operations
              </h2>
              <div className="space-y-4">
                {[
                  { icon: AlertCircle, text: 'HR and admin teams spend hours coordinating late changes and escalations' },
                  { icon: Clock, text: 'Shift-based pickups become difficult to manage across multiple vendors and routes' },
                  { icon: MessageCircle, text: 'Employees keep calling the helpdesk to know where the cab is and when it will arrive' },
                  { icon: TrendingUp, text: 'Costs rise when routes, occupancy, and vendor billing are not monitored centrally' },
                ].map((item, index) => (
                  <div key={index} className="flex items-start gap-4 p-4 bg-gray-50 rounded-xl hover:bg-red-50 transition-colors">
                    <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <item.icon className="w-5 h-5 text-red-500" />
                    </div>
                    <p className="text-gray-700">{item.text}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <div className="bg-gradient-to-br from-primary-50 to-accent-orange/5 rounded-2xl p-8 sm:p-12">
                <div className="text-center">
                  <div className="w-20 h-20 bg-primary-600 rounded-2xl flex items-center justify-center mx-auto mb-6">
                    <Bus className="w-10 h-10 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-navy-950 mb-4">Employee Transport, Simplified</h3>
                  <div className="space-y-3">
                    {[
                      'Live trip visibility for every route and shift',
                      'Automated scheduling reduces daily manual work',
                      'Employee notifications improve confidence and reduce support calls',
                      'Vendor and billing control keep service levels and costs in check',
                    ].map((item, index) => (
                      <div key={index} className="flex items-center gap-3">
                        <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                        <span className="text-gray-700 text-left">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="section-padding bg-gray-50">
        <div className="container-main">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-navy-950 mb-4">
              Powerful Features for Enterprise Commutes
            </h2>
            <p className="text-lg text-gray-600">
              Everything you need to manage employee transport with better visibility, safety, and operational control.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <FeatureCard key={index} feature={feature} index={index} />
            ))}
          </div>
        </div>
      </section>

      <section className="section-padding bg-white">
        <div className="container-main">
          <div className="text-center max-w-3xl mx-auto">
            <p className="text-sm text-gray-500 mb-4 uppercase tracking-widest font-medium">Built for Every Stakeholder</p>
            <h2 className="text-5xl lg:text-6xl font-bold text-navy-950 font-heading leading-[0.9] mb-6">
              DESIGNED FOR<br />
              <span className="text-primary-600">EVERYDAY OPERATIONS</span>
            </h2>
            <p className="text-xl text-gray-600 leading-relaxed">
              Purpose-built experiences for employees, admin teams, and transport operations keep everyone aligned.
            </p>
          </div>
        </div>
      </section>

      <section className="section-padding bg-gray-50 overflow-hidden">
        <div className="container-main">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div className="order-2 lg:order-1">
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-r from-blue-100 to-blue-50 rounded-3xl blur-2xl opacity-70" />
                <div className="relative bg-white rounded-3xl shadow-sm p-4 border border-gray-100">
                  <EmployeeAppMockup />
                </div>
              </div>
            </div>
            <div className="order-1 lg:order-2">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-100 rounded-full mb-6">
                <Users className="w-4 h-4 text-blue-600" />
                <span className="text-sm font-medium text-blue-700">For Employees</span>
              </div>
              <h3 className="text-4xl lg:text-5xl font-bold text-navy-950 font-heading mb-4">
                A Better Commute Experience
              </h3>
              <p className="text-xl text-gray-600 mb-8">
                Give employees clear pickup visibility, timely notifications, and more confidence during every trip.
              </p>
              <div className="grid sm:grid-cols-2 gap-4">
                {[
                  { icon: MapPin, text: 'Track assigned trips and pickup ETAs in real time' },
                  { icon: Bell, text: 'Receive instant updates for delays and arrivals' },
                  { icon: Smartphone, text: 'Simple app experience built for daily commute use' },
                  { icon: Shield, text: 'Improved confidence through visible trip progress' },
                ].map((item, index) => {
                  const Icon = item.icon;
                  return (
                    <div key={index} className="flex items-start gap-3 p-4 bg-white rounded-xl shadow-sm">
                      <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Icon className="w-5 h-5 text-blue-600" />
                      </div>
                      <span className="text-gray-700 font-medium text-sm">{item.text}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="section-padding bg-white overflow-hidden">
        <div className="container-main">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-orange-100 rounded-full mb-6">
                <Briefcase className="w-4 h-4 text-orange-600" />
                <span className="text-sm font-medium text-orange-700">For HR & Admin Teams</span>
              </div>
              <h3 className="text-4xl lg:text-5xl font-bold text-navy-950 font-heading mb-4">
                Run the Desk Without the Chaos
              </h3>
              <p className="text-xl text-gray-600 mb-8">
                Manage rosters, route changes, shift requirements, and service escalations from one operational workspace.
              </p>
              <div className="grid sm:grid-cols-2 gap-4">
                {[
                  { icon: Clock, text: 'Plan routes around shifts and roster changes' },
                  { icon: FileCheck, text: 'Monitor service quality and transport policies' },
                  { icon: MessageCircle, text: 'Reduce employee calls with proactive notifications' },
                  { icon: Building2, text: 'Coordinate transport across campuses and offices' },
                ].map((item, index) => {
                  const Icon = item.icon;
                  return (
                    <div key={index} className="flex items-start gap-3 p-4 bg-gray-50 rounded-xl">
                      <div className="w-10 h-10 bg-orange-50 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Icon className="w-5 h-5 text-orange-600" />
                      </div>
                      <span className="text-gray-700 font-medium text-sm">{item.text}</span>
                    </div>
                  );
                })}
              </div>
            </div>
            <div>
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-r from-orange-100 to-orange-50 rounded-3xl blur-2xl opacity-70" />
                <div className="relative bg-white rounded-3xl shadow-sm p-0 border border-gray-100">
                  <AdminDeskMockup />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="section-padding bg-gray-50 overflow-hidden">
        <div className="container-main">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div className="order-2 lg:order-1">
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-r from-green-100 to-green-50 rounded-3xl blur-2xl opacity-70" />
                <div className="relative bg-white rounded-3xl shadow-sm p-0 border border-gray-100">
                  <ControlTowerMockup />
                </div>
              </div>
            </div>
            <div className="order-1 lg:order-2">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-100 rounded-full mb-6">
                <TrendingUp className="w-4 h-4 text-green-600" />
                <span className="text-sm font-medium text-green-700">For Transport Operations</span>
              </div>
              <h3 className="text-4xl lg:text-5xl font-bold text-navy-950 font-heading mb-4">
                One Control Tower for Daily Execution
              </h3>
              <p className="text-xl text-gray-600 mb-8">
                Keep routes, vendors, trip status, SLA alerts, and cost signals visible in one live operations layer.
              </p>
              <div className="grid sm:grid-cols-2 gap-4">
                {[
                  { icon: Navigation, text: 'Track live route execution and exceptions' },
                  { icon: Zap, text: 'Respond faster to changes and escalations' },
                  { icon: TrendingUp, text: 'Improve utilization and reduce avoidable spend' },
                  { icon: BarChart3, text: 'Review performance, trends, and billing inputs' },
                ].map((item, index) => {
                  const Icon = item.icon;
                  return (
                    <div key={index} className="flex items-start gap-3 p-4 bg-white rounded-xl shadow-sm">
                      <div className="w-10 h-10 bg-green-50 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Icon className="w-5 h-5 text-green-600" />
                      </div>
                      <span className="text-gray-700 font-medium text-sm">{item.text}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="section-padding bg-white">
        <div className="container-main">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-navy-950 mb-4">
              How Employee Transport Works
            </h2>
            <p className="text-lg text-gray-600">
              From onboarding to live execution, every shift follows a clear operational workflow.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-6">
            {[
              { step: '1', title: 'Set Up', desc: 'Add sites, shift timings, employee data, routes, and vendor partners' },
              { step: '2', title: 'Plan', desc: 'Create trip plans around rosters, occupancy, and service rules' },
              { step: '3', title: 'Dispatch', desc: 'Assign vehicles and share trip visibility with employees' },
              { step: '4', title: 'Monitor', desc: 'Track live trips, delays, escalations, and vendor SLA adherence' },
              { step: '5', title: 'Review', desc: 'Reconcile trips, analyze costs, and improve route performance' },
            ].map((item, index) => (
              <div key={index} className="text-center group">
                <div className="relative inline-block">
                  <div className="w-16 h-16 bg-primary-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4 shadow-lg group-hover:scale-110 transition-transform">
                    {item.step}
                  </div>
                  {index < 4 && <div className="hidden lg:block absolute top-1/2 left-full w-full h-0.5 bg-primary-200" />}
                </div>
                <h3 className="font-bold text-navy-950 mb-2">{item.title}</h3>
                <p className="text-sm text-gray-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section-padding bg-gray-50">
        <div className="container-main">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-navy-950 mb-4">
              Everything You Need to Run Enterprise Commutes
            </h2>
            <p className="text-lg text-gray-600">
              A complete operating layer for employee transport, organized around the teams and controls that matter most.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="space-y-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                  <Shield className="w-5 h-5 text-red-600" />
                </div>
                <h3 className="text-xl font-bold text-navy-950">Safety & Compliance</h3>
              </div>
              {[
                { name: 'Trip Visibility', desc: 'Live view of active employee trips' },
                { name: 'Alerts & Escalations', desc: 'Exception handling for delays and issues' },
                { name: 'Audit Trails', desc: 'Clear trip history and operational records' },
                { name: 'Policy Alignment', desc: 'Support safer enterprise commute processes' },
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-4 p-4 bg-white rounded-xl hover:shadow-md transition-all border border-transparent hover:border-gray-100">
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-semibold text-navy-950 text-sm">{item.name}</h4>
                    <p className="text-xs text-gray-500">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="space-y-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Zap className="w-5 h-5 text-blue-600" />
                </div>
                <h3 className="text-xl font-bold text-navy-950">Planning & Dispatch</h3>
              </div>
              {[
                { name: 'Shift Scheduling', desc: 'Support dynamic shift-based transport planning' },
                { name: 'Route Planning', desc: 'Organize pickups, drops, and utilization' },
                { name: 'Employee Notifications', desc: 'Communicate changes automatically' },
                { name: 'Control Desk Tools', desc: 'Run daily operations with less manual effort' },
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-4 p-4 bg-white rounded-xl hover:shadow-md transition-all border border-transparent hover:border-gray-100">
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-semibold text-navy-950 text-sm">{item.name}</h4>
                    <p className="text-xs text-gray-500">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="space-y-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-green-600" />
                </div>
                <h3 className="text-xl font-bold text-navy-950">Vendor & Cost Control</h3>
              </div>
              {[
                { name: 'Vendor Performance', desc: 'Track adherence and service quality' },
                { name: 'SLA Monitoring', desc: 'Keep operations aligned to agreed targets' },
                { name: 'Billing Validation', desc: 'Support cleaner monthly reconciliation' },
                { name: 'Analytics & Reporting', desc: 'Improve decisions with operational data' },
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-4 p-4 bg-white rounded-xl hover:shadow-md transition-all border border-transparent hover:border-gray-100">
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-semibold text-navy-950 text-sm">{item.name}</h4>
                    <p className="text-xs text-gray-500">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-12 bg-white border-y border-gray-100">
        <div className="container-main">
          <div className="text-center mb-10">
            <p className="text-sm font-medium text-gray-500 uppercase tracking-wide">Built for Round-the-Clock Enterprises</p>
          </div>

          <div className="grid md:grid-cols-4 gap-6">
            {[
              { title: 'IT & Tech Parks', desc: 'Reliable commute operations across day and night shifts' },
              { title: 'BPO & Shared Services', desc: 'High-volume shift transport with employee visibility' },
              { title: 'Manufacturing Units', desc: 'Route planning for plant staff and distributed pickup points' },
              { title: 'Multi-Site Enterprises', desc: 'Central transport control across offices and campuses' },
            ].map((useCase, index) => (
              <div key={index} className="card p-6 text-center">
                <div className="w-12 h-12 bg-primary-50 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <Building2 className="w-6 h-6 text-primary-600" />
                </div>
                <h3 className="font-bold text-navy-950 mb-2">{useCase.title}</h3>
                <p className="text-sm text-gray-600">{useCase.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <EmployeeTestimonials />

      <section className="section-padding bg-gray-50">
        <div className="container-main">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-navy-950 mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-lg text-gray-600">
              Everything you need to know about the Employee Transport offering.
            </p>
          </div>
          <div className="max-w-3xl mx-auto space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-white rounded-xl shadow-sm overflow-hidden">
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-gray-50 transition-colors"
                >
                  <span className="font-semibold text-navy-950">{faq.q}</span>
                  {openFaq === index ? (
                    <ChevronUp className="w-5 h-5 text-gray-400" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-400" />
                  )}
                </button>
                {openFaq === index && (
                  <div className="px-6 pb-4">
                    <p className="text-gray-600">{faq.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section-padding bg-white">
        <div className="container-main">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-navy-950 mb-4">
              Flexible Rollout Models for Enterprise Teams
            </h2>
            <p className="text-lg text-gray-600">
              Start with a focused rollout and expand across campuses, vendors, and shift operations as you grow.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {[
              {
                name: 'Pilot',
                description: 'For focused rollout across one site or one transport cluster',
                features: ['Core trip visibility', 'Employee notifications', 'Admin desk controls', 'Standard reporting'],
              },
              {
                name: 'Growth',
                description: 'For multi-route teams scaling across vendors and shifts',
                features: ['Everything in Pilot', 'Shift-based scheduling', 'Vendor monitoring', 'Advanced analytics'],
                popular: true,
              },
              {
                name: 'Enterprise',
                description: 'For large organizations managing multiple sites and policies',
                features: ['Everything in Growth', 'Custom workflows', 'Dedicated support', 'Enterprise rollout planning'],
              },
            ].map((plan, index) => (
              <div key={index} className={`card p-6 relative ${plan.popular ? 'border-2 border-primary-500' : ''}`}>
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <span className="bg-primary-600 text-white text-xs font-semibold px-3 py-1 rounded-full">Most Popular</span>
                  </div>
                )}
                <h3 className="text-xl font-bold text-navy-950 mb-2">{plan.name}</h3>
                <p className="text-gray-600 text-sm mb-6">{plan.description}</p>
                <ul className="space-y-3 mb-6">
                  {plan.features.map((feature, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm">
                      <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>
                <button className={`w-full ${plan.popular ? 'btn-primary' : 'btn-secondary'}`}>Talk to Our Team</button>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="contact" className="section-padding bg-navy-950">
        <div className="container-main">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
              Ready to Upgrade Your Employee Commute Experience?
            </h2>
            <p className="text-lg text-gray-400 mb-10">
              Bring employee visibility, transport desk efficiency, and vendor control into one platform.
              Start with a tailored walkthrough for your routes, shifts, and team structure.
            </p>

            <div className="grid sm:grid-cols-3 gap-6 mb-10">
              <div className="bg-white/10 rounded-xl p-6 text-center hover:bg-white/20 transition-colors">
                <Phone className="w-8 h-8 text-primary-400 mx-auto mb-3" />
                <div className="text-white font-medium mb-1">Call Us</div>
                <div className="text-gray-400 text-sm">+91 99165 00454</div>
              </div>
              <div className="bg-white/10 rounded-xl p-6 text-center hover:bg-white/20 transition-colors">
                <Mail className="w-8 h-8 text-primary-400 mx-auto mb-3" />
                <div className="text-white font-medium mb-1">Email Us</div>
                <div className="text-gray-400 text-sm">info@gaadimatic.com</div>
              </div>
              <div className="bg-white/10 rounded-xl p-6 text-center hover:bg-white/20 transition-colors">
                <Clock className="w-8 h-8 text-primary-400 mx-auto mb-3" />
                <div className="text-white font-medium mb-1">Go-Live Support</div>
                <div className="text-gray-400 text-sm">Onboarding tailored to your operations</div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="#contact" className="btn-primary text-lg px-10 inline-flex items-center justify-center">
                Request Demo
              </a>
              <a href="/pricing" className="inline-flex items-center justify-center px-8 py-4 text-base font-semibold text-white border-2 border-white/30 rounded-lg transition-all duration-200 hover:bg-white/10">
                View Pricing
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default EmployeeTransport;
